import { Disclosure } from "@headlessui/react";
import { ChevronUpIcon } from "@heroicons/react/20/solid";
import { AnimatePresence, motion, useInView } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { IconType } from "react-icons";
import { AiOutlineOpenAI } from "react-icons/ai";
import { DiAndroid, DiCodeBadge } from "react-icons/di";
import {
  FaAws,
  FaCloud,
  FaDatabase,
  FaLinux,
  FaPython,
  FaReact,
} from "react-icons/fa";
import { GrVulnerability } from "react-icons/gr";
import { IoLogoJavascript } from "react-icons/io5";
import { RiFlutterFill } from "react-icons/ri";
import { SiAdobe, SiApple, SiKotlin } from "react-icons/si";
import { Tooltip } from "react-tooltip";
import { useContact } from "../contexts/contactContext";
import useSmoothScroll from "../hooks/useSmoothScroll";
import { STANDARD_VARIANTS } from "../constants/animations";
import { CONTAINER_STYLES, TYPOGRAPHY_STYLES, CARD_STYLES } from "../constants/styles";

const minWidthForDesktop = 768;

interface ServiceIcon {
  icon: IconType;
  label: string;
}

interface Service {
  title: string;
  description: string;
  icons: ServiceIcon[];
}

const services: Service[] = [
  {
    title: "Mobile App Development",
    description:
      "Android, iOS, and Flutter apps taken from concept to store release. My own apps have passed 20 million downloads; I build yours with the same care.",
    icons: [
      { icon: RiFlutterFill, label: "Flutter/Dart" },
      { icon: SiKotlin, label: "Kotlin + Compose" },
      { icon: DiAndroid, label: "Android" },
      { icon: SiApple, label: "iOS" },
    ],
  },
  {
    title: "AI & Machine Learning Solutions",
    description:
      "Production AI systems, not demos: LLM integrations, NLP pipelines, and the infrastructure that keeps them fast and affordable at scale. I build the AI backend serving millions of Mixtiles users.",
    icons: [
      { icon: AiOutlineOpenAI, label: "AI Integration" },
      { icon: FaPython, label: "Python" },
      { icon: FaCloud, label: "Cloud" },
      { icon: FaDatabase, label: "SQL/Redis" },
    ],
  },
  {
    title: "Full-Stack Web Development",
    description:
      "Frontends, backends, and deployment: React on the surface, Python or Node behind it, AWS underneath. One person who can own the whole stack.",
    icons: [
      { icon: FaReact, label: "ReactJS" },
      { icon: IoLogoJavascript, label: "JavaScript/TypeScript" },
      { icon: FaPython, label: "Python" },
      { icon: FaAws, label: "AWS" },
      { icon: SiAdobe, label: "Adobe Suite" },
    ],
  },
  {
    title: "Cybersecurity & Infrastructure",
    description:
      "Security reviews, vulnerability research, and secure architecture design, grounded in years of cybersecurity R&D in the IDF's Unit 8200.",
    icons: [
      { icon: FaCloud, label: "Network Security" },
      { icon: DiCodeBadge, label: "Cyber-Oriented Development" },
      { icon: GrVulnerability, label: "Vulnerability Research" },
      { icon: FaLinux, label: "Linux" },
    ],
  },
];

const IconsDisplay: React.FC<{ icons: ServiceIcon[]; groupId: string }> = ({
  icons,
  groupId,
}) => (
  <div className="flex mt-8 space-x-4 justify-center">
    {icons.map(({ icon: Icon, label }) => {
      const tooltipId = `icon-tooltip-${groupId}-${label}`;
      return (
        <div key={label} className="relative">
          <Icon
            data-tooltip-id={tooltipId}
            data-tooltip-content={label}
            aria-label={label}
            className="w-7 h-7 hover:scale-125 transition-all duration-100 ease-in-out text-primary-600 dark:text-primary-400"
          />
          <Tooltip id={tooltipId} />
        </div>
      );
    })}
  </div>
);

const CardContent: React.FC<Service & { isDesktop: boolean }> = ({
  title,
  description,
  icons,
  isDesktop,
}) => (
  <div className="px-6 py-2 flex flex-col h-full duration-100 transition-all ease-in-out">
    {isDesktop ? (
      <>
        <h3 className="text-2xl font-semibold mb-4 leading-tight">{title}</h3>
        <p className="flex-grow">{description}</p>
        <IconsDisplay icons={icons} groupId={title} />
      </>
    ) : (
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button className="flex justify-between w-full py-4 text-xl font-medium text-left focus:outline-none focus-visible:ring focus-visible:ring-primary-500 focus-visible:ring-opacity-75">
              <span>{title}</span>
              <ChevronUpIcon
                className={`${!open ? "transform rotate-180" : ""
                  } w-5 h-5 text-accent-500 transition-transform duration-200`}
              />
            </Disclosure.Button>
            <AnimatePresence>
              {open && (
                <Disclosure.Panel static>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="text-accent-800 dark:text-accent-200">
                      {description}
                      <IconsDisplay icons={icons} groupId={title} />
                    </div>
                  </motion.div>
                </Disclosure.Panel>
              )}
            </AnimatePresence>
          </>
        )}
      </Disclosure>
    )}
  </div>
);

const TechStack: React.FC = () => {
  const { setTitle, setMessage } = useContact();
  const scrollToElement = useSmoothScroll();

  const handleServiceClick = (serviceTitle: string) => {
    setTitle(`${serviceTitle} project`);
    setMessage(`Hi Tomer,\nI'm looking for help with ${serviceTitle.toLowerCase()}.`);
    scrollToElement("contact");
  };

  const [isDesktop, setIsDesktop] = useState(
    window.innerWidth >= minWidthForDesktop
  );

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  useEffect(() => {
    const handleResize = () =>
      setIsDesktop(window.innerWidth >= minWidthForDesktop);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  return (
    <motion.div
      ref={ref}
      className={CONTAINER_STYLES.full}
      animate={isInView ? "visible" : "hidden"}
      variants={STANDARD_VARIANTS.container}
    >
      <motion.h2
        className={TYPOGRAPHY_STYLES.sectionTitle}
        variants={STANDARD_VARIANTS.item}
      >
        Services
      </motion.h2>
      <motion.p
        className={`${TYPOGRAPHY_STYLES.sectionSubtitle} mb-4`}
        variants={STANDARD_VARIANTS.item}
      >
        I work with startups and established teams to design, build, and ship software that lasts.
      </motion.p>
      <motion.p
        className="text-lg text-center mb-12 dark:text-accent-400 text-accent-600 max-w-2xl mx-auto"
        variants={STANDARD_VARIANTS.item}
      >
        Have a project in mind?{" "}
        <a
          href="#contact"
          onClick={(e) => {
            e.preventDefault();
            scrollToElement("contact");
          }}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-semibold cursor-pointer underline decoration-2 underline-offset-2"
        >
          Let's talk
        </a>
      </motion.p>
      <motion.div
        className={`xl:mx-64 grid ${"md:px-0 px-8 md:grid-cols-2 grid-cols-1 gap-6"}`}
        variants={STANDARD_VARIANTS.container}
      >
        {services.map((service, index) => (
          <motion.div
            key={index}
            className={`${isDesktop ? "hover:scale-105 hover:shadow-xl" : ""
              } transition-all duration-300 ease-in-out py-2 md:py-8 ${CARD_STYLES.base} dark:text-surface-50 text-accent-900 overflow-hidden cursor-pointer`}
            onClick={() => handleServiceClick(service.title)}
            variants={STANDARD_VARIANTS.item}
          >
            <CardContent {...service} isDesktop={isDesktop} />
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

export default TechStack;
